import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MapView, { isEmergency } from "./components/MapView.jsx";
import DetailsPanel from "./components/DetailsPanel.jsx";
import AirportPanel from "./components/AirportPanel.jsx";
import FlightSearch from "./components/FlightSearch.jsx";
import RunwayView from "./components/RunwayView.jsx";
import StatsBar from "./components/StatsBar.jsx";
import FilterBar from "./components/FilterBar.jsx";
import HelpOverlay from "./components/HelpOverlay.jsx";
import HudCanvas from "./hud/HudCanvas.jsx";
import SettingsPanel from "./settings/SettingsPanel.jsx";
import { useSettings } from "./settings/useSettings.jsx";
import CockpitView from "./cockpit/CockpitView.jsx";
import Preview3D from "./preview/Preview3D.jsx";
import { SAMPLE_AIRPORTS, airportsInBbox } from "./lib/airportsSample.js";
import { haversineKm } from "./lib/geo.js";
import ClockWidget from "./components/widgets/ClockWidget.jsx";
import MiniRadar from "./components/widgets/MiniRadar.jsx";
import Leaderboard from "./components/widgets/Leaderboard.jsx";
import InstrumentTape from "./components/widgets/InstrumentTape.jsx";
import { permissionState, requestPermission, notify, subscribeToPush } from "./lib/notify.js";
import { applyTheme, loadThemeId, getTheme } from "./themes.js";

const REFRESH_MS = 15_000;
const COCKPIT_REFRESH_MS = 3_000; // poll faster in the cockpit for live instruments

const loadWatched = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem("watchedCallsigns") || "[]"));
  } catch {
    return new Set();
  }
};

export default function App() {
  const [bbox, setBbox] = useState(null);
  const [aircraft, setAircraft] = useState([]);
  const [provider, setProvider] = useState(null);
  const [status, setStatus] = useState("Starting up…");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [detailAirport, setDetailAirport] = useState(null); // fullscreen runway/airport view
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [view, setView] = useState("map"); // "map" | "list"
  const [themeId, setThemeId] = useState(loadThemeId);
  const [helpOpen, setHelpOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [hudOpen, setHudOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false); // 3D route preview overlay
  const [flightSearchOpen, setFlightSearchOpen] = useState(false); // weeks-ahead fare search
  const [previewRoute, setPreviewRoute] = useState(null); // {origin, destination} or null (manual entry)
  const [previewDeparture, setPreviewDeparture] = useState(null); // Date used for the preview's sky
  const [focusTarget, setFocusTarget] = useState(null); // {lat, lon, zoom} to fly the map to
  const { settings } = useSettings();
  const [filterPredicate, setFilterPredicate] = useState(() => () => true);
  const [notifPerm, setNotifPerm] = useState(permissionState());

  // Apply the selected theme (CSS variables) whenever it changes.
  useEffect(() => {
    applyTheme(themeId);
  }, [themeId]);
  const mapStyle = getTheme(themeId).map;
  const [watched, setWatched] = useState(loadWatched);

  const bboxRef = useRef(bbox);
  bboxRef.current = bbox;
  const abortRef = useRef(null);
  const debounceRef = useRef(null);
  const seenEmergencyRef = useRef(new Set());
  const seenWatchedRef = useRef(new Set());
  const watchedRef = useRef(watched);
  watchedRef.current = watched;

  // Persist the watch list.
  useEffect(() => {
    localStorage.setItem("watchedCallsigns", JSON.stringify([...watched]));
  }, [watched]);

  const enableNotifications = useCallback(async () => {
    const state = await requestPermission();
    setNotifPerm(state);
    if (state === "granted") {
      subscribeToPush(); // no-op until VAPID keys are configured on the server
      notify("Notifications on", { body: "You'll be alerted about emergencies and watched flights." });
    }
  }, []);

  const handleSelectAirport = useCallback((ap) => {
    setSelectedAirport(ap);
    setSelectedId(null); // only one detail panel at a time
    setView("map");
    setFocusTarget({ lat: ap.lat, lon: ap.lon, zoom: 11 });
  }, []);

  // Open the 3D route preview. `route` (with origin/destination coords) previews a
  // known flight; passing null opens the manual "plan a flight" entry form.
  const openPreview = useCallback((route) => {
    setPreviewRoute(route || null);
    setPreviewDeparture(route ? new Date() : null); // manual mode picks its own time
    setPreviewOpen(true);
  }, []);

  // "I'm feeling lucky": pick a random future flight that departs from an airport
  // currently in the map view (falling back to the nearest ones to the centre),
  // to a far-off destination, 1–3 days ahead. Returns { origin, destination,
  // departure } for the preview to fly.
  const feelingLucky = useCallback(() => {
    const b = bboxRef.current;
    let origins = airportsInBbox(b);
    if (origins.length === 0 && b) {
      const cLat = (+b.lamin + +b.lamax) / 2;
      const cLon = (+b.lomin + +b.lomax) / 2;
      origins = [...SAMPLE_AIRPORTS]
        .sort(
          (a1, a2) =>
            haversineKm(cLat, cLon, a1.latitude, a1.longitude) -
            haversineKm(cLat, cLon, a2.latitude, a2.longitude)
        )
        .slice(0, 4);
    }
    if (origins.length === 0) origins = SAMPLE_AIRPORTS;
    const origin = origins[Math.floor(Math.random() * origins.length)];

    // Destination: favour somewhere genuinely far for a proper long flight.
    const far = SAMPLE_AIRPORTS.filter((a) => a.icao !== origin.icao)
      .map((a) => ({ a, d: haversineKm(origin.latitude, origin.longitude, a.latitude, a.longitude) }))
      .filter((x) => x.d > 800)
      .sort((x, y) => y.d - x.d);
    const pool = (far.length ? far.slice(0, 20) : SAMPLE_AIRPORTS.filter((a) => a.icao !== origin.icao).map((a) => ({ a })));
    const destination = pool[Math.floor(Math.random() * pool.length)].a;

    const days = 1 + Math.floor(Math.random() * 3); // 1, 2 or 3 days ahead
    const departure = new Date(Date.now() + days * 86_400_000);
    departure.setHours(6 + Math.floor(Math.random() * 14), 0, 0, 0);

    return { origin, destination, departure };
  }, []);

  // Selecting an aircraft (from map, list, radar or search) closes the airport panel.
  useEffect(() => {
    if (selectedId) setSelectedAirport(null);
  }, [selectedId]);

  const toggleWatch = useCallback((callsign) => {
    const cs = (callsign || "").trim();
    if (!cs) return;
    setWatched((prev) => {
      const next = new Set(prev);
      next.has(cs) ? next.delete(cs) : next.add(cs);
      return next;
    });
  }, []);

  // Fire notifications when an emergency aircraft or a watched flight newly
  // appears. We track "already alerted" ids so we only notify on the transition.
  const checkAlerts = useCallback((list) => {
    if (permissionState() !== "granted") return;
    const emSeen = seenEmergencyRef.current;
    const wSeen = seenWatchedRef.current;
    const currentEm = new Set();
    const currentW = new Set();
    const watchList = watchedRef.current;

    for (const ac of list) {
      if (isEmergency(ac)) {
        currentEm.add(ac.icao24);
        if (!emSeen.has(ac.icao24)) {
          notify("⚠️ Emergency aircraft nearby", {
            body: `${ac.callsign || ac.icao24} is squawking ${ac.squawk}.`,
            tag: `em-${ac.icao24}`,
          });
        }
      }
      const cs = (ac.callsign || "").trim();
      if (cs && watchList.has(cs)) {
        currentW.add(cs);
        if (!wSeen.has(cs)) {
          notify(`✈️ ${cs} is in view`, {
            body: "A flight you're watching just appeared on the map.",
            tag: `watch-${cs}`,
          });
        }
      }
    }
    seenEmergencyRef.current = currentEm;
    seenWatchedRef.current = currentW;
  }, []);

  const fetchStates = useCallback(async () => {
    const b = bboxRef.current;
    if (!b) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const params = new URLSearchParams(b);
      const res = await fetch(`/api/states?${params}`, { signal: ctrl.signal });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      const list = data.aircraft || [];
      checkAlerts(list);
      setAircraft(list);
      setProvider(data.provider || null);
      setStatus(
        `${(data.aircraft || []).length} aircraft in view · updated just now` +
          (data.provider ? ` · via ${data.provider}` : "")
      );
    } catch (e) {
      if (e.name !== "AbortError") setStatus(`Could not load flights: ${e.message}`);
    }
  }, []);

  // Debounce map movement, then fetch.
  const handleBounds = useCallback(
    (b) => {
      setBbox(b);
      bboxRef.current = b;
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(fetchStates, 400);
    },
    [fetchStates]
  );

  // Periodic refresh — interval comes from user settings (default REFRESH_MS).
  // In the cockpit we poll faster so the live instruments update smoothly.
  useEffect(() => {
    const base = settings?.refreshMs || REFRESH_MS;
    const ms = view === "cockpit" ? Math.min(base, COCKPIT_REFRESH_MS) : Math.max(3000, base);
    const id = setInterval(fetchStates, ms);
    return () => clearInterval(id);
  }, [fetchStates, settings?.refreshMs, view]);

  // Load enrichment details when an aircraft is selected.
  useEffect(() => {
    if (!selectedId) {
      setDetails(null);
      return;
    }
    const ac = aircraft.find((a) => a.icao24 === selectedId);
    setDetails(null);
    setDetailsLoading(true);
    const params = ac?.callsign ? `?callsign=${encodeURIComponent(ac.callsign)}` : "";
    fetch(`/api/aircraft/${selectedId}${params}`)
      .then((r) => r.json())
      .then((d) => setDetails(d))
      .catch(() => setDetails(null))
      .finally(() => setDetailsLoading(false));
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedAircraft = useMemo(
    () => aircraft.find((a) => a.icao24 === selectedId) || null,
    [aircraft, selectedId]
  );

  // Aircraft actually shown (after the filter bar). Alerts still use the full list.
  const shownAircraft = useMemo(() => {
    try {
      return aircraft.filter(filterPredicate);
    } catch {
      return aircraft;
    }
  }, [aircraft, filterPredicate]);

  // Centre of the current viewport, for the mini-radar widget.
  const mapCenter = useMemo(() => {
    if (!bbox) return null;
    return { lat: (+bbox.lamin + +bbox.lamax) / 2, lon: (+bbox.lomin + +bbox.lomax) / 2 };
  }, [bbox]);

  // HUD widget panels (draggable/dockable over the map).
  const hudPanels = useMemo(
    () => [
      { id: "clock", title: "Clock", node: <ClockWidget />, defaultRect: { x: 24, y: 80, w: 240, h: 150 } },
      {
        id: "radar",
        title: "Mini radar",
        node: <MiniRadar aircraft={shownAircraft} center={mapCenter} selectedId={selectedId} onSelect={setSelectedId} />,
        defaultRect: { x: 24, y: 250, w: 260, h: 300 },
      },
      {
        id: "instruments",
        title: "Instruments",
        node: <InstrumentTape aircraft={selectedAircraft} />,
        defaultRect: { x: 300, y: 80, w: 300, h: 180 },
      },
      {
        id: "leaderboard",
        title: "Leaderboard",
        node: <Leaderboard aircraft={shownAircraft} onSelect={setSelectedId} />,
        defaultRect: { x: 300, y: 280, w: 300, h: 220 },
      },
      {
        id: "stats",
        title: "Stats",
        node: <StatsBar aircraft={shownAircraft} />,
        defaultRect: { x: 620, y: 80, w: 340, h: 180 },
        defaultOpen: false,
      },
    ],
    [shownAircraft, mapCenter, selectedId, selectedAircraft]
  );

  // Search: highlight matching aircraft by callsign or registration.
  const searchMatch = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const set = new Set();
    for (const a of aircraft) {
      if (
        (a.callsign && a.callsign.toLowerCase().includes(q)) ||
        (a.registration && a.registration.toLowerCase().includes(q)) ||
        a.icao24.includes(q)
      ) {
        set.add(a.icao24);
      }
    }
    return set;
  }, [query, aircraft]);

  const onSearchSubmit = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;

    // 1) If it's already on screen, just select it.
    if (searchMatch && searchMatch.size > 0) {
      const id = [...searchMatch][0];
      const ac = aircraft.find((a) => a.icao24 === id);
      setSelectedId(id);
      if (ac) setFocusTarget({ lat: ac.latitude, lon: ac.longitude, zoom: 9 });
      return;
    }

    // 2) Otherwise look it up worldwide and fly there.
    setStatus(`Searching worldwide for "${q}"…`);
    try {
      const res = await fetch(`/api/find?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      const found = (data.aircraft || [])[0];
      if (!found) {
        setStatus(`No flight found for "${q}". It may not be broadcasting right now.`);
        return;
      }
      // Inject so it's immediately selectable; the next viewport fetch keeps it fresh.
      setAircraft((prev) => {
        const rest = prev.filter((a) => a.icao24 !== found.icao24);
        return [found, ...rest];
      });
      setSelectedId(found.icao24);
      setView("map");
      setFocusTarget({ lat: found.latitude, lon: found.longitude, zoom: 9 });
      setStatus(`Found ${found.callsign || found.icao24}`);
    } catch (err) {
      setStatus(`Search failed: ${err.message}`);
    }
  };

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="logo" aria-hidden="true">✈</span>
          <h1>Plane Spotter</h1>
        </div>
        <form className="search" onSubmit={onSearchSubmit} role="search">
          <label htmlFor="q" className="sr-only">
            Search by flight callsign or registration
          </label>
          <input
            id="q"
            type="search"
            placeholder="Search a flight (e.g. BAW123)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
          />
          <button type="submit">Find</button>
        </form>
        <div className="viewtoggle" role="group" aria-label="Choose map or list view">
          <button
            className={view === "map" ? "active" : ""}
            aria-pressed={view === "map"}
            onClick={() => setView("map")}
          >
            🗺️ Map
          </button>
          <button
            className={view === "cockpit" ? "active" : ""}
            aria-pressed={view === "cockpit"}
            onClick={() => setView("cockpit")}
            title="Photo-real glass cockpit driven by the selected aircraft"
          >
            🛩️ Cockpit
          </button>
        </div>

        <div className="tools" role="group" aria-label="Tools">
          {notifPerm !== "unsupported" && (
            <button
              className="iconbtn"
              onClick={enableNotifications}
              disabled={notifPerm === "granted"}
              aria-label={notifPerm === "granted" ? "Notifications are on" : "Turn on alerts"}
              title={
                notifPerm === "granted"
                  ? "Notifications on"
                  : notifPerm === "denied"
                  ? "Notifications blocked"
                  : "Turn on alerts"
              }
            >
              {notifPerm === "denied" ? "🔕" : "🔔"}
            </button>
          )}
          {settings?.preview3d !== false && (
            <button
              className="iconbtn"
              onClick={() => openPreview(null)}
              aria-label="Preview a flight in 3D"
              title="Preview a flight in 3D (plan a route)"
            >
              🛫
            </button>
          )}
          <button
            className={flightSearchOpen ? "iconbtn active" : "iconbtn"}
            aria-pressed={flightSearchOpen}
            onClick={() => setFlightSearchOpen(true)}
            aria-label="Find a flight (fares weeks ahead)"
            title="Find a flight — fares & schedules weeks ahead"
          >
            🎫
          </button>
          <button
            className={hudOpen ? "iconbtn active" : "iconbtn"}
            aria-pressed={hudOpen}
            onClick={() => setHudOpen((o) => !o)}
            aria-label="Toggle HUD workspace"
            title="HUD workspace"
          >
            🎛️
          </button>
          <button
            className="iconbtn"
            onClick={() => setSettingsOpen(true)}
            aria-label="Open settings"
            title="Settings"
          >
            ⚙️
          </button>
          <button
            className="iconbtn"
            onClick={() => setHelpOpen(true)}
            aria-label="Help"
            title="Help"
          >
            ❓
          </button>
        </div>

        <p className="status" aria-live="polite">
          {searchMatch ? `${searchMatch.size} match${searchMatch.size === 1 ? "" : "es"} · ` : ""}
          {status}
        </p>
      </header>

      <StatsBar aircraft={shownAircraft} collapsible />
      <FilterBar onChange={setFilterPredicate} />

      <main className="stage">
        {view === "map" && (
          <MapView
            aircraft={shownAircraft}
            selected={selectedId}
            searchMatch={searchMatch}
            mapStyle={mapStyle}
            focusTarget={focusTarget}
            onSelect={setSelectedId}
            onSelectAirport={handleSelectAirport}
            onBoundsChange={handleBounds}
          />
        )}
        {view === "cockpit" && (
          <CockpitView
            aircraft={selectedAircraft}
            details={details}
            traffic={shownAircraft}
            onExit={() => setView("map")}
          />
        )}
        {hudOpen && <HudCanvas panels={hudPanels} storageKey="hud-v1" />}
        {selectedAircraft && view !== "cockpit" && (
          <DetailsPanel
            aircraft={selectedAircraft}
            details={details}
            loading={detailsLoading}
            watched={selectedAircraft.callsign && watched.has(selectedAircraft.callsign.trim())}
            onToggleWatch={() => toggleWatch(selectedAircraft.callsign)}
            onOpenCockpit={() => setView("cockpit")}
            onPreview={settings?.preview3d !== false ? openPreview : undefined}
            onClose={() => setSelectedId(null)}
          />
        )}
        {selectedAirport && (
          <AirportPanel
            airport={selectedAirport}
            onClose={() => setSelectedAirport(null)}
            onViewDetail={setDetailAirport}
          />
        )}
        {detailAirport && (
          <RunwayView airport={detailAirport} onClose={() => setDetailAirport(null)} />
        )}
      </main>

      <Preview3D
        open={previewOpen}
        route={previewRoute}
        departureTime={previewDeparture || undefined}
        terrain={settings?.previewTerrain !== false}
        onLucky={feelingLucky}
        onClose={() => setPreviewOpen(false)}
      />

      <FlightSearch open={flightSearchOpen} onClose={() => setFlightSearchOpen(false)} />

      <HelpOverlay open={helpOpen} onClose={() => setHelpOpen(false)} />
      <SettingsPanel
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        themeId={themeId}
        onThemeChange={setThemeId}
      />
    </div>
  );
}
